import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesAPI, customersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { printTransaction } from '../lib/printDoc';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
    Receipt, 
    Banknote, 
    CreditCard, 
    Building2, 
    Clock, 
    Loader2,
    Check,
    Trash2,
    FileCheck,
    Calendar as CalendarIcon,
    MoreHorizontal,
    Building,
    User,
    Phone,
    Mail,
    MapPin,
    Plus,
    X,
    Split,
    ArrowLeft,
    Printer,
} from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';

const paymentMethods = [
    { value: 'cash', label: 'Cash', icon: Banknote, color: 'bg-[#2A9D8F]' },
    { value: 'card', label: 'Card', icon: CreditCard, color: 'bg-[#264653]' },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: Building2, color: 'bg-[#E9C46A]' },
    { value: 'cheque', label: 'Cheque', icon: FileCheck, color: 'bg-[#8B5CF6]' },
    { value: 'due', label: 'Due', icon: Clock, color: 'bg-[#E76F51]' },
    { value: 'others', label: 'Others', icon: MoreHorizontal, color: 'bg-[#6B7280]' },
];

const RecordSale = () => {
    const navigate = useNavigate();
    // Invoice & Amount
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [notes, setNotes] = useState('');
    
    // Customer master
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');

    // Company Details
    const [companyName, setCompanyName] = useState('');
    const [companyAddress, setCompanyAddress] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    
    // Split Payment Mode
    const [isSplitPayment, setIsSplitPayment] = useState(false);
    
    // Single Payment Mode
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [chequeDepositDate, setChequeDepositDate] = useState(null);
    const [chequeBankName, setChequeBankName] = useState('');
    const [chequeNumber, setChequeNumber] = useState('');
    
    // Split Payments Array
    const [payments, setPayments] = useState([]);
    
    const [loading, setLoading] = useState(false);
    const [recentSales, setRecentSales] = useState([]);
    const [fetchingRecent, setFetchingRecent] = useState(true);

    useEffect(() => {
        fetchRecentSales();
        fetchNextInvoiceNumber();
        fetchCustomers();
    }, []);

    const fetchNextInvoiceNumber = async () => {
        try {
            const { data } = await salesAPI.nextInvoiceNumber();
            setInvoiceNumber(data.next);
        } catch (e) {
            console.error('Failed to fetch invoice number', e);
        }
    };

    const fetchCustomers = async () => {
        try {
            const { data } = await customersAPI.getAll();
            setCustomers(data);
        } catch (e) {
            console.error('Failed to fetch customers', e);
        }
    };

    const handleCustomerSelect = (id) => {
        setSelectedCustomerId(id);
        if (id === '__new__') {
            setCompanyName('');
            setCompanyAddress('');
            setContactPerson('');
            setMobileNumber('');
            setEmailAddress('');
            return;
        }
        const c = customers.find((x) => x.id === id);
        if (c) {
            setCompanyName(c.company_name || '');
            setCompanyAddress(c.company_address || '');
            setContactPerson(c.contact_person || '');
            setMobileNumber(c.mobile_number || '');
            setEmailAddress(c.email_address || '');
        }
    };

    const fetchRecentSales = async () => {
        try {
            const today = new Date().toISOString().split('T')[0];
            const response = await salesAPI.getAll({ start_date: today });
            setRecentSales(response.data);
        } catch (error) {
            console.error('Failed to fetch recent sales:', error);
        } finally {
            setFetchingRecent(false);
        }
    };

    const resetForm = () => {
        fetchNextInvoiceNumber();
        setNotes('');
        setSelectedCustomerId('');
        setCompanyName('');
        setCompanyAddress('');
        setContactPerson('');
        setMobileNumber('');
        setEmailAddress('');
        setAmount('');
        setPaymentMethod('');
        setChequeDepositDate(null);
        setChequeBankName('');
        setChequeNumber('');
        setIsSplitPayment(false);
        setPayments([]);
    };

    // Add new payment method for split payment
    const addPayment = () => {
        setPayments([...payments, { 
            method: '', 
            amount: '', 
            cheques: [] 
        }]);
    };

    // Remove payment method
    const removePayment = (index) => {
        setPayments(payments.filter((_, i) => i !== index));
    };

    // Update payment
    const updatePayment = (index, field, value) => {
        const updated = [...payments];
        updated[index][field] = value;
        // Reset cheques if method changed from cheque
        if (field === 'method' && value !== 'cheque') {
            updated[index].cheques = [];
        }
        setPayments(updated);
    };

    // Add cheque to a payment
    const addCheque = (paymentIndex) => {
        const updated = [...payments];
        updated[paymentIndex].cheques.push({
            bank_name: '',
            cheque_number: '',
            deposit_date: null,
            amount: ''
        });
        setPayments(updated);
    };

    // Remove cheque
    const removeCheque = (paymentIndex, chequeIndex) => {
        const updated = [...payments];
        updated[paymentIndex].cheques = updated[paymentIndex].cheques.filter((_, i) => i !== chequeIndex);
        setPayments(updated);
    };

    // Update cheque
    const updateCheque = (paymentIndex, chequeIndex, field, value) => {
        const updated = [...payments];
        updated[paymentIndex].cheques[chequeIndex][field] = value;
        setPayments(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSplitPayment) {
            // Validate split payments
            if (payments.length < 2) {
                toast.error('Split payment requires at least 2 payment methods');
                return;
            }

            for (let i = 0; i < payments.length; i++) {
                const payment = payments[i];
                if (!payment.method) {
                    toast.error(`Please select payment method for payment ${i + 1}`);
                    return;
                }
                if (!payment.amount || parseFloat(payment.amount) <= 0) {
                    toast.error(`Please enter a valid amount for payment ${i + 1}`);
                    return;
                }
                if (payment.method === 'cheque') {
                    if (payment.cheques.length === 0) {
                        toast.error(`Please add at least one cheque for payment ${i + 1}`);
                        return;
                    }
                    const chequeTotal = payment.cheques.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
                    if (Math.abs(chequeTotal - parseFloat(payment.amount)) > 0.01) {
                        toast.error(`Cheque amounts (€${chequeTotal.toFixed(2)}) must equal payment amount (€${parseFloat(payment.amount).toFixed(2)}) for payment ${i + 1}`);
                        return;
                    }
                    for (let j = 0; j < payment.cheques.length; j++) {
                        const cheque = payment.cheques[j];
                        if (!cheque.bank_name || !cheque.cheque_number || !cheque.deposit_date || !cheque.amount) {
                            toast.error(`Please fill all cheque details for cheque ${j + 1} in payment ${i + 1}`);
                            return;
                        }
                    }
                }
            }

            setLoading(true);
            try {
                const saleData = {
                    invoice_number: invoiceNumber,
                    amount: payments.reduce((sum, p) => sum + parseFloat(p.amount), 0),
                    notes: notes,
                    company_name: companyName,
                    company_address: companyAddress,
                    contact_person: contactPerson,
                    mobile_number: mobileNumber,
                    email_address: emailAddress,
                    payments: payments.map(p => ({
                        method: p.method,
                        amount: parseFloat(p.amount),
                        cheques: p.method === 'cheque' ? p.cheques.map(c => ({
                            bank_name: c.bank_name,
                            cheque_number: c.cheque_number,
                            deposit_date: format(c.deposit_date, 'yyyy-MM-dd'),
                            amount: parseFloat(c.amount)
                        })) : null
                    }))
                };

                await salesAPI.create(saleData);
                toast.success('Split payment sale recorded!', {
                    action: {
                        label: 'Print Receipt',
                        onClick: () => printTransaction({ ...saleData, invoice_number: invoiceNumber, type: 'sale', created_at: new Date().toISOString() }),
                    },
                });
                resetForm();
                fetchRecentSales();
            } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to record sale');
            } finally {
                setLoading(false);
            }
        } else {
            // Single payment mode
            if (!amount || !paymentMethod) {
                toast.error('Please fill in all required fields');
                return;
            }

            const amountNum = parseFloat(amount);
            if (isNaN(amountNum) || amountNum <= 0) {
                toast.error('Please enter a valid amount');
                return;
            }

            if (paymentMethod === 'cheque') {
                if (!chequeDepositDate) {
                    toast.error('Please select a deposit date for the cheque');
                    return;
                }
                if (!chequeBankName) {
                    toast.error('Please enter the bank name');
                    return;
                }
                if (!chequeNumber) {
                    toast.error('Please enter the cheque number');
                    return;
                }
            }

            setLoading(true);
            try {
                const saleData = {
                    invoice_number: invoiceNumber,
                    amount: amountNum,
                    payment_method: paymentMethod,
                    notes: notes,
                    company_name: companyName,
                    company_address: companyAddress,
                    contact_person: contactPerson,
                    mobile_number: mobileNumber,
                    email_address: emailAddress,
                };

                if (paymentMethod === 'cheque') {
                    saleData.cheque_deposit_date = format(chequeDepositDate, 'yyyy-MM-dd');
                    saleData.cheque_bank_name = chequeBankName;
                    saleData.cheque_number = chequeNumber;
                }

                await salesAPI.create(saleData);
                toast.success('Sale recorded!', {
                    action: {
                        label: 'Print Receipt',
                        onClick: () => printTransaction({ ...saleData, invoice_number: invoiceNumber, type: 'sale', created_at: new Date().toISOString() }),
                    },
                });
                resetForm();
                fetchRecentSales();
            } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to record sale');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDelete = async (saleId) => {
        if (!window.confirm('Are you sure you want to delete this sale?')) return;
        
        try {
            await salesAPI.delete(saleId);
            toast.success('Sale deleted');
            fetchRecentSales();
        } catch (error) {
            toast.error('Failed to delete sale');
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(value || 0);
    };

    const getPaymentBadge = (method, sale = null) => {
        if (method === 'split' && sale?.payments) {
            return (
                <Badge className="bg-gradient-to-r from-[#2A9D8F] to-[#8B5CF6] text-white font-medium">
                    Split
                </Badge>
            );
        }
        const pm = paymentMethods.find(p => p.value === method);
        if (!pm) return null;
        
        const colorMap = {
            cash: 'badge-cash',
            card: 'badge-card',
            bank_transfer: 'badge-bank',
            cheque: 'bg-[#8B5CF6] text-white',
            due: 'badge-due',
            others: 'bg-[#6B7280] text-white',
        };
        
        return (
            <Badge className={`${colorMap[method]} font-medium`}>
                {pm.label}
            </Badge>
        );
    };

    const getTotalAmount = () => {
        if (isSplitPayment) {
            return payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        }
        return parseFloat(amount) || 0;
    };

    return (
        <div data-testid="record-sale-page" className="space-y-6 animate-fadeIn">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/sales')}
                        data-testid="back-to-choice"
                        className="-ml-3 mb-1 text-gray-500 hover:text-[#1A1A1A]"
                    >
                        <ArrowLeft size={16} className="mr-1" /> Back
                    </Button>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">Record a Sale</h1>
                    <p className="text-gray-500 mt-1">Quick sale entry · no line items, money already received</p>
                </div>
                <div className="px-4 py-2 bg-[#1A1A1A] text-white rounded-lg">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400">Invoice No.</p>
                    <p className="font-mono font-bold text-lg" data-testid="auto-invoice-number">{invoiceNumber || '...'}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                {/* Sale Form */}
                <div className="xl:col-span-2">
                    <Card className="border-0 shadow-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <span className="flex items-center gap-2">
                                    <Receipt className="text-[#C08478]" size={20} />
                                    Sale Details
                                </span>
                                {/* Split Payment Toggle */}
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="split-mode" className="text-sm font-normal text-gray-600">
                                        Split Payment
                                    </Label>
                                    <Switch 
                                        id="split-mode"
                                        data-testid="split-payment-toggle"
                                        checked={isSplitPayment}
                                        onCheckedChange={(checked) => {
                                            setIsSplitPayment(checked);
                                            if (checked && payments.length === 0) {
                                                setPayments([
                                                    { method: '', amount: '', cheques: [] },
                                                    { method: '', amount: '', cheques: [] }
                                                ]);
                                            }
                                        }}
                                    />
                                </div>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Invoice & Company Details Row */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Left Column - Amount */}
                                    <div className="space-y-4">
                                        {/* Show total amount for split payment or amount field for single */}
                                        {isSplitPayment ? (
                                            <div className="p-4 bg-gradient-to-r from-[#C08478]/10 to-[#8B5CF6]/10 rounded-xl border border-[#C08478]/20">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-medium text-gray-600">Total Amount</span>
                                                    <span className="text-2xl font-bold text-[#1A1A1A]">
                                                        {formatCurrency(getTotalAmount())}
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <Label htmlFor="amount" className="text-sm font-medium">
                                                    Amount (€) <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="amount"
                                                    data-testid="sale-amount"
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={amount}
                                                    onChange={(e) => setAmount(e.target.value)}
                                                    placeholder="0.00"
                                                    className="h-11 text-lg font-semibold bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478]"
                                                />
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">Existing Customer</Label>
                                            <Select value={selectedCustomerId} onValueChange={handleCustomerSelect}>
                                                <SelectTrigger
                                                    data-testid="customer-select"
                                                    className="h-11 bg-[#F5F5F5] border-[#E5E5E5]"
                                                >
                                                    <SelectValue placeholder="Pick existing or add new..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__new__">+ New customer</SelectItem>
                                                    {customers.map((c) => (
                                                        <SelectItem key={c.id} value={c.id}>
                                                            {c.company_name}
                                                            {c.contact_person ? ` · ${c.contact_person}` : ''}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    {/* Right Column - Company Details */}
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium flex items-center gap-1">
                                                <Building size={14} /> Company Name
                                            </Label>
                                            <Input
                                                data-testid="company-name"
                                                value={companyName}
                                                onChange={(e) => setCompanyName(e.target.value)}
                                                placeholder="Company name"
                                                className="h-11 bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478]"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium flex items-center gap-1">
                                                <MapPin size={14} /> Address
                                            </Label>
                                            <Input
                                                data-testid="company-address"
                                                value={companyAddress}
                                                onChange={(e) => setCompanyAddress(e.target.value)}
                                                placeholder="Company address"
                                                className="h-11 bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478]"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Details Row */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm font-medium flex items-center gap-1">
                                            <User size={14} /> Contact Person
                                        </Label>
                                        <Input
                                            data-testid="contact-person"
                                            value={contactPerson}
                                            onChange={(e) => setContactPerson(e.target.value)}
                                            placeholder="Contact name"
                                            className="h-11 bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478]"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-sm font-medium flex items-center gap-1">
                                            <Phone size={14} /> Mobile Number
                                        </Label>
                                        <Input
                                            data-testid="mobile-number"
                                            value={mobileNumber}
                                            onChange={(e) => setMobileNumber(e.target.value)}
                                            placeholder="+33 6 12 34 56 78"
                                            className="h-11 bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478]"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-sm font-medium flex items-center gap-1">
                                            <Mail size={14} /> Email Address
                                        </Label>
                                        <Input
                                            data-testid="email-address"
                                            type="email"
                                            value={emailAddress}
                                            onChange={(e) => setEmailAddress(e.target.value)}
                                            placeholder="email@company.com"
                                            className="h-11 bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478]"
                                        />
                                    </div>
                                </div>

                                {/* Payment Section */}
                                {isSplitPayment ? (
                                    /* Split Payment Mode */
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-sm font-medium flex items-center gap-2">
                                                <Split size={16} className="text-[#8B5CF6]" />
                                                Payment Methods <span className="text-red-500">*</span>
                                            </Label>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={addPayment}
                                                className="text-[#C08478] border-[#C08478] hover:bg-[#C08478]/10"
                                            >
                                                <Plus size={14} className="mr-1" /> Add Payment
                                            </Button>
                                        </div>

                                        <div className="space-y-4">
                                            {payments.map((payment, paymentIndex) => (
                                                <div key={paymentIndex} className="p-4 bg-[#F5F5F5] rounded-xl border border-[#E5E5E5]">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <span className="text-sm font-medium text-gray-600">
                                                            Payment {paymentIndex + 1}
                                                        </span>
                                                        {payments.length > 2 && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removePayment(paymentIndex)}
                                                                className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                                                            >
                                                                <X size={14} />
                                                            </Button>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {/* Payment Method Selection */}
                                                        <div className="space-y-2">
                                                            <Label className="text-xs text-gray-500">Method</Label>
                                                            <div className="grid grid-cols-3 gap-1">
                                                                {paymentMethods.map((pm) => (
                                                                    <button
                                                                        key={pm.value}
                                                                        type="button"
                                                                        data-testid={`split-payment-method-${paymentIndex}-${pm.value}`}
                                                                        onClick={() => updatePayment(paymentIndex, 'method', pm.value)}
                                                                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${
                                                                            payment.method === pm.value
                                                                                ? 'border-[#C08478] bg-[#C08478]/10'
                                                                                : 'border-gray-200 hover:border-gray-300 bg-white'
                                                                        }`}
                                                                    >
                                                                        <div className={`w-6 h-6 rounded ${pm.color} flex items-center justify-center`}>
                                                                            <pm.icon size={12} className="text-white" />
                                                                        </div>
                                                                        <span className="text-[10px] font-medium text-[#1A1A1A]">{pm.label}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Amount */}
                                                        <div className="space-y-2">
                                                            <Label className="text-xs text-gray-500">Amount (€)</Label>
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                data-testid={`split-payment-amount-${paymentIndex}`}
                                                                value={payment.amount}
                                                                onChange={(e) => updatePayment(paymentIndex, 'amount', e.target.value)}
                                                                placeholder="0.00"
                                                                className="h-10 font-semibold bg-white"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Cheque Details for this payment */}
                                                    {payment.method === 'cheque' && (
                                                        <div className="mt-4 p-3 bg-[#8B5CF6]/5 rounded-lg border border-[#8B5CF6]/20">
                                                            <div className="flex items-center justify-between mb-3">
                                                                <span className="text-sm font-medium text-[#8B5CF6] flex items-center gap-1">
                                                                    <FileCheck size={14} />
                                                                    Cheques
                                                                </span>
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => addCheque(paymentIndex)}
                                                                    className="text-[#8B5CF6] border-[#8B5CF6] hover:bg-[#8B5CF6]/10 h-7 text-xs"
                                                                >
                                                                    <Plus size={12} className="mr-1" /> Add Cheque
                                                                </Button>
                                                            </div>

                                                            {payment.cheques.map((cheque, chequeIndex) => (
                                                                <div key={chequeIndex} className="mb-3 p-3 bg-white rounded-lg border">
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <span className="text-xs font-medium text-gray-500">
                                                                            Cheque {chequeIndex + 1}
                                                                        </span>
                                                                        <Button
                                                                            type="button"
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => removeCheque(paymentIndex, chequeIndex)}
                                                                            className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                                                        >
                                                                            <X size={12} />
                                                                        </Button>
                                                                    </div>
                                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                                        <Input
                                                                            placeholder="Bank Name"
                                                                            value={cheque.bank_name}
                                                                            onChange={(e) => updateCheque(paymentIndex, chequeIndex, 'bank_name', e.target.value)}
                                                                            className="h-9 text-sm"
                                                                        />
                                                                        <Input
                                                                            placeholder="Cheque No."
                                                                            value={cheque.cheque_number}
                                                                            onChange={(e) => updateCheque(paymentIndex, chequeIndex, 'cheque_number', e.target.value)}
                                                                            className="h-9 text-sm font-mono"
                                                                        />
                                                                        <Popover>
                                                                            <PopoverTrigger asChild>
                                                                                <Button
                                                                                    variant="outline"
                                                                                    className={`h-9 text-sm justify-start ${!cheque.deposit_date && 'text-gray-400'}`}
                                                                                >
                                                                                    <CalendarIcon className="mr-1 h-3 w-3" />
                                                                                    {cheque.deposit_date ? format(cheque.deposit_date, 'dd/MM/yy') : 'Date'}
                                                                                </Button>
                                                                            </PopoverTrigger>
                                                                            <PopoverContent className="w-auto p-0" align="start">
                                                                                <Calendar
                                                                                    mode="single"
                                                                                    selected={cheque.deposit_date}
                                                                                    onSelect={(date) => updateCheque(paymentIndex, chequeIndex, 'deposit_date', date)}
                                                                                    initialFocus
                                                                                />
                                                                            </PopoverContent>
                                                                        </Popover>
                                                                        <Input
                                                                            type="number"
                                                                            step="0.01"
                                                                            placeholder="Amount €"
                                                                            value={cheque.amount}
                                                                            onChange={(e) => updateCheque(paymentIndex, chequeIndex, 'amount', e.target.value)}
                                                                            className="h-9 text-sm font-semibold"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            ))}

                                                            {payment.cheques.length === 0 && (
                                                                <p className="text-xs text-gray-400 text-center py-2">
                                                                    Click "Add Cheque" to add cheque details
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    /* Single Payment Mode */
                                    <>
                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">
                                                Payment Method <span className="text-red-500">*</span>
                                            </Label>
                                            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                                                {paymentMethods.map((pm) => (
                                                    <button
                                                        key={pm.value}
                                                        type="button"
                                                        data-testid={`payment-method-${pm.value}`}
                                                        onClick={() => {
                                                            setPaymentMethod(pm.value);
                                                            if (pm.value !== 'cheque') {
                                                                setChequeDepositDate(null);
                                                                setChequeBankName('');
                                                                setChequeNumber('');
                                                            }
                                                        }}
                                                        className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                                                            paymentMethod === pm.value
                                                                ? 'border-[#C08478] bg-[#C08478]/5'
                                                                : 'border-[#E5E5E5] hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <div className={`w-9 h-9 rounded-lg ${pm.color} flex items-center justify-center`}>
                                                            <pm.icon size={16} className="text-white" />
                                                        </div>
                                                        <span className="text-xs font-medium text-[#1A1A1A]">{pm.label}</span>
                                                        {paymentMethod === pm.value && (
                                                            <Check size={14} className="text-[#C08478] absolute top-1 right-1" />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Single Cheque Details */}
                                        {paymentMethod === 'cheque' && (
                                            <div className="space-y-4 p-4 bg-[#8B5CF6]/5 rounded-xl border border-[#8B5CF6]/20 animate-fadeIn">
                                                <h4 className="font-medium text-[#8B5CF6] flex items-center gap-2">
                                                    <FileCheck size={16} />
                                                    Cheque Details
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="space-y-2">
                                                        <Label className="text-sm font-medium">
                                                            Bank Name <span className="text-red-500">*</span>
                                                        </Label>
                                                        <Input
                                                            data-testid="cheque-bank-name"
                                                            value={chequeBankName}
                                                            onChange={(e) => setChequeBankName(e.target.value)}
                                                            placeholder="e.g., BNP Paribas"
                                                            className="h-11 bg-white border-[#E5E5E5] focus:border-[#8B5CF6]"
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <Label className="text-sm font-medium">
                                                            Cheque Number <span className="text-red-500">*</span>
                                                        </Label>
                                                        <Input
                                                            data-testid="cheque-number"
                                                            value={chequeNumber}
                                                            onChange={(e) => setChequeNumber(e.target.value)}
                                                            placeholder="e.g., 0001234"
                                                            className="h-11 font-mono bg-white border-[#E5E5E5] focus:border-[#8B5CF6]"
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <Label className="text-sm font-medium">
                                                            Deposit Date <span className="text-red-500">*</span>
                                                        </Label>
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <Button
                                                                    variant="outline"
                                                                    data-testid="cheque-deposit-date"
                                                                    className={`w-full h-11 justify-start text-left font-normal bg-white border-[#E5E5E5] ${
                                                                        !chequeDepositDate && 'text-gray-500'
                                                                    }`}
                                                                >
                                                                    <CalendarIcon className="mr-2 h-4 w-4 text-[#8B5CF6]" />
                                                                    {chequeDepositDate ? (
                                                                        format(chequeDepositDate, 'dd MMM yyyy')
                                                                    ) : (
                                                                        'Select date'
                                                                    )}
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-auto p-0" align="start">
                                                                <Calendar
                                                                    mode="single"
                                                                    selected={chequeDepositDate}
                                                                    onSelect={setChequeDepositDate}
                                                                    initialFocus
                                                                />
                                                            </PopoverContent>
                                                        </Popover>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Notes */}
                                <div className="space-y-2">
                                    <Label htmlFor="notes" className="text-sm font-medium">
                                        Notes (Optional)
                                    </Label>
                                    <Textarea
                                        id="notes"
                                        data-testid="sale-notes"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Add any additional notes..."
                                        className="bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478] min-h-[60px]"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    data-testid="sale-submit"
                                    disabled={loading}
                                    className="w-full h-12 bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full font-medium transition-all hover:shadow-lg"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            Recording...
                                        </>
                                    ) : (
                                        isSplitPayment ? `Record Split Payment (${formatCurrency(getTotalAmount())})` : 'Record Sale'
                                    )}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>

                {/* Today's Sales */}
                <div className="xl:col-span-1">
                    <Card className="border-0 shadow-sm">
                        <CardHeader>
                            <CardTitle>Today's Sales</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {fetchingRecent ? (
                                <div className="flex items-center justify-center h-48">
                                    <Loader2 className="w-6 h-6 animate-spin text-[#C08478]" />
                                </div>
                            ) : recentSales.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                                    <Receipt size={40} className="mb-2 opacity-50" />
                                    <p>No sales recorded today</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-[#F5F5F5]">
                                                <TableHead className="font-medium">Invoice</TableHead>
                                                <TableHead className="font-medium">Amount</TableHead>
                                                <TableHead className="font-medium">Method</TableHead>
                                                <TableHead></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {recentSales.slice(0, 10).map((sale) => (
                                                <TableRow key={sale.id} className="hover:bg-[#FAFAFA]">
                                                    <TableCell className="font-mono text-xs">
                                                        {sale.invoice_number}
                                                    </TableCell>
                                                    <TableCell className="font-semibold text-sm euro-amount">
                                                        {formatCurrency(sale.amount)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {getPaymentBadge(sale.payment_method, sale)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleDelete(sale.id)}
                                                            className="text-gray-400 hover:text-red-500 h-7 w-7"
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default RecordSale;
